import React from 'react';
import { cva } from 'class-variance-authority';
import clsx from 'clsx';
import { createRoot } from 'react-dom/client';
import { motion, AnimatePresence } from 'motion/react';
import { Icon } from './Icon';
import { zIndex as Z } from '../../tokens/zIndex';
import { useReducedMotion } from '../../hooks/useReducedMotion';
import { toastEntrance } from './primitives/motion';
import styles from './Notification.module.css';

type NotificationType = 'info' | 'success' | 'warning' | 'error';
type NotificationPlacement = 'topRight' | 'topLeft' | 'bottomRight' | 'bottomLeft';

interface NotificationConfig {
  message: React.ReactNode;
  description?: React.ReactNode;
  type?: NotificationType;
  placement?: NotificationPlacement;
  duration?: number;
  key?: string;
  btn?: React.ReactNode;
  icon?: React.ReactNode;
  onClose?: () => void;
}

interface NotificationInstance {
  destroy: () => void;
}

const typeIcons: Record<NotificationType, React.ReactNode> = {
  info: <Icon name="info" size={20} />,
  success: <Icon name="check_circle" size={20} />,
  warning: <Icon name="warning" size={20} />,
  error: <Icon name="cancel" size={20} />,
};

const typeIconColorClass: Record<NotificationType, string> = {
  info: styles.iconInfo,
  success: styles.iconSuccess,
  warning: styles.iconWarning,
  error: styles.iconError,
};

const notificationItem = cva(styles.item);

function NotificationItem({
  config,
  onRemove,
  isLeft: _isLeft,
}: {
  config: NotificationConfig;
  onRemove: () => void;
  isLeft: boolean;
}) {
  const reducedMotion = useReducedMotion();

  const iconColorClass = config.type ? typeIconColorClass[config.type] : styles.iconDefault;
  const iconNode = config.icon ?? (config.type ? typeIcons[config.type] : null);

  return (
    <motion.div
      initial={reducedMotion ? { opacity: 0 } : toastEntrance.initial}
      animate={reducedMotion ? { opacity: 1 } : toastEntrance.animate}
      exit={reducedMotion ? { opacity: 0 } : toastEntrance.exit}
      transition={reducedMotion ? { duration: 0 } : toastEntrance.transition}
      className={notificationItem()}
    >
      {iconNode && (
        <span className={clsx(styles.iconWrapper, iconColorClass)}>
          {iconNode}
        </span>
      )}
      <div className={styles.content}>
        <div className={clsx(styles.notifTitle, config.description && styles.notifTitleWithDesc)}>
          {config.message}
        </div>
        {config.description && (
          <div className={styles.notifDescription}>
            {config.description}
          </div>
        )}
        {config.btn && <div className={styles.notifBtn}>{config.btn}</div>}
      </div>
      <button
        onClick={onRemove}
        className={styles.closeBtn}
        title="Close"
      >
        <Icon name="close" size={14} />
      </button>
    </motion.div>
  );
}

// ── Container management per placement ───────────────────────────────────────

const containers = new Map<NotificationPlacement, { el: HTMLDivElement; root: ReturnType<typeof createRoot> }>();
const notifs = new Map<NotificationPlacement, { id: number; config: NotificationConfig; remove: () => void }[]>();
let nextId = 0;

const placementClass: Record<NotificationPlacement, string> = {
  topRight: styles.topRight,
  topLeft: styles.topLeft,
  bottomRight: styles.bottomRight,
  bottomLeft: styles.bottomLeft,
};

function ensureContainer(placement: NotificationPlacement) {
  if (!containers.has(placement)) {
    const el = document.createElement('div');
    // Apply both base class and placement-specific class
    el.className = `${styles.containerBase} ${placementClass[placement]}`;
    // z-index via CSS custom property
    el.style.setProperty('--notif-z', String(Z.toast));
    document.body.appendChild(el);
    const root = createRoot(el);
    containers.set(placement, { el, root });
    notifs.set(placement, []);
  }
  return containers.get(placement)!;
}

function renderNotifs(placement: NotificationPlacement) {
  const container = containers.get(placement);
  const items = notifs.get(placement);
  if (!container || !items) return;

  const isLeft = placement.includes('Left');
  container.root.render(
    <AnimatePresence>
      {items.map((n) => (
        <NotificationItem key={n.id} config={n.config} onRemove={n.remove} isLeft={isLeft} />
      ))}
    </AnimatePresence>,
  );
}

/**
 * notification — programmatic notification popups.
 *
 * | Method | Description |
 * |--------|-------------|
 * | notification.open(config) | Show a notification |
 * | notification.info(config) | Info notification |
 * | notification.success(config) | Success notification |
 * | notification.warning(config) | Warning notification |
 * | notification.error(config) | Error notification |
 *
 * Config: `{ message, description?, type?, placement?, duration?, btn?, icon?, onClose? }`
 *
 * Default placement: 'topRight'. Default duration: 4500ms.
 *
 * Returns `{ destroy() }` to manually dismiss.
 *
 * @example
 * notification.success({ message: 'Saved!', description: 'Your thought has been saved.' });
 * notification.error({ message: 'Error', description: 'Failed to sync.', placement: 'bottomRight' });
 */
function openNotification(config: NotificationConfig): NotificationInstance {
  const placement = config.placement ?? 'topRight';
  const duration = config.duration ?? 4500;
  ensureContainer(placement);

  const id = nextId++;
  let timer: ReturnType<typeof setTimeout> | null = null;

  const remove = () => {
    const items = notifs.get(placement) ?? [];
    notifs.set(
      placement,
      items.filter((n) => n.id !== id),
    );
    renderNotifs(placement);
    config.onClose?.();
  };

  const items = notifs.get(placement) ?? [];
  notifs.set(placement, [...items, { id, config, remove }]);
  renderNotifs(placement);

  if (duration > 0) {
    timer = setTimeout(remove, duration);
  }

  return {
    destroy: () => {
      if (timer) clearTimeout(timer);
      remove();
    },
  };
}

export const notification = {
  open: (config: NotificationConfig) => openNotification(config),
  info: (config: Omit<NotificationConfig, 'type'>) => openNotification({ ...config, type: 'info' }),
  success: (config: Omit<NotificationConfig, 'type'>) => openNotification({ ...config, type: 'success' }),
  warning: (config: Omit<NotificationConfig, 'type'>) => openNotification({ ...config, type: 'warning' }),
  error: (config: Omit<NotificationConfig, 'type'>) => openNotification({ ...config, type: 'error' }),
};
